import { REACT_APP_BACKEND_BASE_URL } from "configs/config";
import { BaseApiService } from "services/BaseApiService";
import type {
  EnterpriseSalesInquiryStatusEnum,
  IEnterpriseSalesInquiryCreateRequest,
  IEnterpriseSalesInquiryCreateResponse,
  IEnterpriseSalesInquiryListResponse,
  IEnterpriseSalesInquiryUpdateStatusRequest,
} from "types/muggleTest/enterpriseSalesInquiry";

const ENTERPRISE_SALES_INQUIRIES_ENDPOINT = "/protected/muggle-test/enterprise-sales-inquiries";
const ENTERPRISE_SALES_INQUIRIES_PUBLIC_ENDPOINT = "/public/enterprise-sales-inquiries";

export class EnterpriseSalesInquiryService extends BaseApiService {
  public static async createInquiryAsync(params: {
    authToken: string;
    request: IEnterpriseSalesInquiryCreateRequest;
  }): Promise<IEnterpriseSalesInquiryCreateResponse> {
    const { authToken, request } = params;
    return this.postAsync<IEnterpriseSalesInquiryCreateResponse>(
      ENTERPRISE_SALES_INQUIRIES_ENDPOINT,
      authToken,
      request
    );
  }

  public static async createPublicInquiryAsync(params: {
    request: IEnterpriseSalesInquiryCreateRequest;
  }): Promise<IEnterpriseSalesInquiryCreateResponse> {
    const { request } = params;

    const response = await fetch(`${REACT_APP_BACKEND_BASE_URL}${ENTERPRISE_SALES_INQUIRIES_PUBLIC_ENDPOINT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      let backendMessage = "";
      try {
        const errorBody = (await response.json()) as { error?: string };
        backendMessage = errorBody.error ?? "";
      } catch {
        backendMessage = "";
      }
      throw new Error(backendMessage || `Public enterprise inquiry request failed: ${response.statusText}`);
    }

    return (await response.json()) as IEnterpriseSalesInquiryCreateResponse;
  }

  public static async listInquiriesAsync(params: {
    authToken: string;
    scope?: "mine" | "all";
    status?: EnterpriseSalesInquiryStatusEnum;
    limit?: number;
    offset?: number;
  }): Promise<IEnterpriseSalesInquiryListResponse> {
    const { authToken, scope = "mine", status, limit, offset } = params;
    const url = new URL(ENTERPRISE_SALES_INQUIRIES_ENDPOINT, "http://local");
    url.searchParams.set("scope", scope);
    if (status) {
      url.searchParams.set("status", status);
    }
    if (limit !== undefined) {
      url.searchParams.set("limit", String(limit));
    }
    if (offset !== undefined) {
      url.searchParams.set("offset", String(offset));
    }

    return this.getAsync<IEnterpriseSalesInquiryListResponse>(`${url.pathname}${url.search}`, authToken);
  }

  public static async updateInquiryStatusAsync(params: {
    authToken: string;
    inquiryId: string;
    request: IEnterpriseSalesInquiryUpdateStatusRequest;
  }): Promise<void> {
    const { authToken, inquiryId, request } = params;
    await this.fetchWithAuthAsync<void>(`${ENTERPRISE_SALES_INQUIRIES_ENDPOINT}/${inquiryId}/status`, authToken, {
      method: "PATCH",
      body: JSON.stringify(request),
    });
  }

  public static async deleteInquiryAsync(params: {
    authToken: string;
    inquiryId: string;
  }): Promise<void> {
    const { authToken, inquiryId } = params;
    await this.deleteAsync<void>(`${ENTERPRISE_SALES_INQUIRIES_ENDPOINT}/${inquiryId}`, authToken);
  }
}
